import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { Link, ResponsiveImage } from "@quintype/components";
import { shape, string, object, number, bool } from "prop-types";
import { formatToMonthYear } from "../../utils/date-formatter";
import { STATIC_KEYWORDS } from "../../../constants";
import { getSectionUrl } from "../../../utils/story-utils";
import "./research-card.m.css";

/**
 * ResearchCard component displays a single story card in the Research Hub
 * Features:
 * - Story image with attribute icon overlay
 * - Category tag
 * - Headline with description
 * - Publication date in "mmm yyyy" format
 * - Tag name below the card
 */
export const ResearchCard = ({ story, position, backgroundColor, showBottomBorder, hideImage }) => {

  const fallbackImage = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "fallback-image"], "")
  );
  const showImagePlaceholder = useSelector((state) => get(state, ["qt", "config", "showPlaceholder"]));
  const getPlaceholderStyleName = showImagePlaceholder ? "placeholder" : "";
  const {
    "published-at": publishedAt,
    "first-published-at": firstPublishedAt,
    "hero-image-s3-key": heroImageS3Key,
    "hero-image-metadata": heroImageMetadata,
    headline: storyHeadline,
    subheadline,
    slug,
  } = story || {};
  const alternateHeadline = get(story, ["alternative", "home", "default", "headline"]);
  const headline = alternateHeadline || storyHeadline;

  // Image precedence: Alternate Image → Hero Image → Fallback Image
  const alternateImageS3Key = get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"]);
  const imageS3Key = alternateImageS3Key || heroImageS3Key;

  if (!story) return null;

  // Get the first section/tag for display
  const { name: sectionName, slug: sectionSlug } = story?.sections?.[0] || {};
  const reportTag = story?.tags?.find(tag => tag.type === "report-type");
  const [svgUrl, setSvgUrl] = useState("");

  useEffect(() => {
    if (reportTag?.id) {
      fetch(`/api/v1/entities/${reportTag.id}`)
        .then(res => res.json())
        .then(data => {
          const url = data?.entity?.["desktop-svg"]?.[0]?.url;
          if (url) setSvgUrl(url);
        })
        .catch(err => console.error("Error fetching entity:", err));
    }
  }, [reportTag?.id]);

  // Format publication date
  const formattedDate = formatToMonthYear(publishedAt || firstPublishedAt);

  // Check if story is premium
  const isPremiumStory = story?.["story-template"] === "premium-story-template";

  const isBrandCard = !!backgroundColor;
  const wrapperClasses = [
    "research-card-wrapper",
    showBottomBorder && "research-card-wrapper-with-border",
    hideImage && "research-card-wrapper-no-image",
    isBrandCard && "research-card-brand"
  ].filter(Boolean).join(" ");
  const contentStyle = backgroundColor ? { backgroundColor, paddingLeft: '20px', paddingRight: '20px' } : {};

  return (
    <Link href={`/${slug}`} className="research-card" aria-label={storyHeadline}>
      <div styleName={wrapperClasses}>
        <figure styleName={`research-card-image ${getPlaceholderStyleName}`}>
          {imageS3Key ? (
            <ResponsiveImage
              slug={imageS3Key}
              metadata={heroImageMetadata}
              aspectRatio={[16, 9]}
              defaultWidth={336}
              widths={[336, 480, 640]}
              imgParams={{ auto: ["format", "compress"] }}
              eager={position < 3 ? "above-fold" : "below-fold"}
              alt={storyHeadline || ""}
            />
          ) : (
            <img
              src={fallbackImage || "/icons/fallback_img.png"}
              alt={storyHeadline || "Fallback image"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover"
              }}
            />
          )}
          {svgUrl && (
            <img
              styleName="research-card-attribute-icon"
              src={svgUrl}
              alt={reportTag?.properties?.["desktop-svg"]?.[0]?.caption}
            />
          )}
        </figure>

        <div styleName="research-card-content" style={contentStyle}>
          {svgUrl && (
            <div styleName="research-card-svg-icon">
              <img src={svgUrl} alt={reportTag?.properties?.["desktop-svg"]?.[0]?.caption} />
            </div>
          )}

          {sectionName && sectionSlug && (
            <Link
              styleName="research-card-category"
              href={getSectionUrl(story?.sections?.[0])}
            >
              {sectionName.toUpperCase()}
            </Link>
          )}

          <div styleName="research-card-text-group">
            <h3 styleName="research-card-headline">{headline}</h3>
            {subheadline && (
              <p styleName="research-card-description">{subheadline}</p>
            )}
          </div>

          <div styleName="research-card-footer">
            {formattedDate && <div styleName="research-card-date">{formattedDate}</div>}
            {isPremiumStory && <div styleName="research-card-premium">{STATIC_KEYWORDS.PREMIUM}</div>}
          </div>
        </div>
      </div>
    </Link>
  );
};

ResearchCard.propTypes = {
  story: shape({
    id: string,
    slug: string,
    headline: string,
    subheadline: string,
    "hero-image-s3-key": string,
    "hero-image-metadata": object,
    "published-at": number,
    "first-published-at": number,
    sections: object,
    tags: object,
    metadata: object,
  }),
  position: number,
  backgroundColor: string,
  showBottomBorder: bool,
  hideImage: bool,
};
